/**
 * App configuration — environment-aware settings.
 *
 * In development: VITE_API_URL is empty, Vite proxy handles /api → localhost:8000
 * In production:  VITE_API_URL = "https://dukkan-api.azurewebsites.net"
 */

export const API_URL = (import.meta as any).env?.VITE_API_URL || '';

// ============================================================================
// Direct Blob Storage Access (bypasses API proxy for fast data loading)
// ============================================================================

/**
 * Is this SAS still usable?
 *
 * A SAS carries its own expiry in the `se=` parameter, so a cached one can be
 * checked without storing a separate timestamp. Tokens are cached in
 * localStorage and now expire in an hour, so a returning visitor would
 * otherwise sign every blob URL with a dead token — every image 403s and the
 * thumbnail fallback chain walks the whole candidate list for nothing.
 *
 * The 60s margin keeps a token that dies mid-flight from being used.
 */
function sasIsValid(sas: string | null): boolean {
  if (!sas) return false
  try {
    const expiry = new URLSearchParams(sas).get('se')
    if (!expiry) return false // no expiry to verify — don't trust it
    return Date.parse(expiry) - Date.now() > 60_000
  } catch {
    return false
  }
}

/** Read a cached SAS, discarding it if it has expired. */
function cachedSas(key: string): string | null {
  if (typeof window === 'undefined') return null
  let stored: string | null = null
  try { stored = localStorage.getItem(key) } catch { return null }
  if (sasIsValid(stored)) return stored
  try { localStorage.removeItem(key) } catch {}
  return null
}

let _blobBaseUrl: string | null = 'https://vshopship.blob.core.windows.net/scan-data'
let _blobSasToken: string | null = cachedSas('dukkan_blob_sas_token')
let _blobTokenFetched = false

// scan-frames container (mobile uploads)
let _framesBaseUrl: string | null = 'https://vshopship.blob.core.windows.net/scan-frames'
let _framesSasToken: string | null = cachedSas('dukkan_frames_sas_token')

// The SAS tokens are container-wide read grants, so the server issues them with
// a 1-hour expiry. Re-fetch well before that or a long browsing session starts
// getting 403s from Azure on every frame.
const BLOB_TOKEN_REFRESH_MS = 45 * 60 * 1000
let _blobRefreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Initialize direct blob access by fetching a container-level SAS token.
 * Also fetches the scan-frames token for mobile-uploaded scans.
 * Called at app startup and again on a timer as the tokens near expiry.
 * If it fails, falls back to API proxy.
 */
export async function initBlobDirectAccess(): Promise<void> {
  if (_blobTokenFetched) return
  _blobTokenFetched = true
  const headers = authHeaders()

  // Both endpoints now require a signed-in user, so a token issued while signed
  // out is never valid — schedule the refresh that picks up the new session.
  if (_blobRefreshTimer) clearTimeout(_blobRefreshTimer)
  _blobRefreshTimer = setTimeout(() => {
    _blobTokenFetched = false
    initBlobDirectAccess()
  }, BLOB_TOKEN_REFRESH_MS)

  // scan-data (COLMAP results)
  try {
    const response = await fetch(apiUrl('/api/scan-data-token'), { headers })
    if (response.ok) {
      const data = await response.json()
      _blobBaseUrl = data.base_url || _blobBaseUrl
      _blobSasToken = data.sas_token
      try { localStorage.setItem('dukkan_blob_sas_token', data.sas_token) } catch {}
      console.log(`⚡ [config] Direct blob access enabled: ${_blobBaseUrl}`)
    } else {
      console.warn(`⚠️ [config] Failed to get blob token (${response.status}), falling back to API proxy`)
    }
  } catch (error) {
    console.warn(`⚠️ [config] Failed to get blob token, falling back to API proxy:`, error)
  }

  // scan-frames (mobile uploads)
  try {
    const response = await fetch(apiUrl('/api/scan-frames-token'), { headers })
    if (response.ok) {
      const data = await response.json()
      _framesBaseUrl = data.baseUrl || _framesBaseUrl
      _framesSasToken = data.sasToken
      try { localStorage.setItem('dukkan_frames_sas_token', data.sasToken) } catch {}
      console.log(`⚡ [config] Scan-frames direct access enabled: ${_framesBaseUrl}`)
    }
  } catch {
    // non-critical — mobile scans will still load if container is public
  }
}

/**
 * Force the next initBlobDirectAccess() to re-fetch.
 *
 * Call after signing in: any token fetched while signed out was refused, and
 * without this the cached "already fetched" flag would keep it that way for
 * the rest of the session.
 */
export async function refreshBlobDirectAccess(): Promise<void> {
  _blobTokenFetched = false
  await initBlobDirectAccess()
}

/**
 * Check if direct blob access is available.
 */
export function hasBlobDirectAccess(): boolean {
  return _blobBaseUrl !== null && _blobSasToken !== null
}

/** Return the blob container base URL (no trailing slash, no SAS token). */
export function getBlobBaseUrl(): string | null { return _blobBaseUrl }

/** Return the raw SAS token string (without leading '?'). */
export function getBlobSasToken(): string | null { return _blobSasToken }

/**
 * Prefix a path with the API server URL.
 * e.g. apiUrl('/api/shops') → "https://dukkan-api.azurewebsites.net/api/shops" in prod
 *                            → "/api/shops" in dev
 */
export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

/**
 * Authorization header for the current session, or {} when signed out.
 *
 * Reads localStorage rather than the auth store because several callers run at
 * module scope or before the store has hydrated — the store would still be
 * empty and the request would go out unauthenticated.
 */
export function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('dukkan_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * fetch() with the session token attached.
 *
 * Shop content endpoints require Google sign-in, so anything reading shops,
 * datasets, posters or blob tokens must go through this.
 */
export function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  return fetch(input, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  });
}

/**
 * Build a shop poster URL carrying the session token.
 *
 * The poster endpoint requires sign-in, and an <img> tag cannot send an
 * Authorization header — hence the `token` query parameter the API accepts.
 */
export function posterUrl(shopId: string, params: Record<string, string> = {}): string {
  const qs = new URLSearchParams(params);
  const token = localStorage.getItem('dukkan_token');
  if (token) qs.set('token', token);
  const query = qs.toString();
  return apiUrl(`/api/shops/${encodeURIComponent(shopId)}/poster${query ? `?${query}` : ''}`);
}

/**
 * Resolve a data URL for scan data files.
 * 
 * When direct blob access is enabled, converts /api/scan-data/ paths to direct
 * Azure Blob Storage URLs (without SAS — use toBlobUrl() for the final URL).
 * This is safe against further path appending (e.g., frames dir + filename).
 */
export function resolveUrl(path: string): string {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  
  // Direct blob access: convert known prefixes to direct Azure Blob Storage URLs
  // SAS token is NOT appended here — it's added by toBlobUrl() at fetch/img time
  if (_blobBaseUrl && _blobSasToken) {
    if (path.startsWith('/api/scan-data/')) {
      const blobPath = path.substring('/api/scan-data/'.length)
      return `${_blobBaseUrl}/${blobPath}`
    }
    // /reconstructions/ paths map to the same blob container (server treats both identically)
    if (path.startsWith('/reconstructions/')) {
      const blobPath = path.substring('/reconstructions/'.length)
      return `${_blobBaseUrl}/${blobPath}`
    }
  }
  
  // Fallback to API proxy
  if (!API_URL) return path;
  if (path.startsWith('/api/')) return `${API_URL}${path}`;
  // /reconstructions/ fallback: route through /api/scan-data/ proxy
  if (path.startsWith('/reconstructions/')) {
    const blobPath = path.substring('/reconstructions/'.length)
    return `${API_URL}/api/scan-data/${blobPath}`;
  }
  return path;
}

/**
 * Check if a URL points to Azure Blob Storage (scan-data or scan-frames container).
 */
export function isBlobUrl(url: string): boolean {
  return (!!_blobBaseUrl && url.startsWith(_blobBaseUrl)) ||
         (!!_framesBaseUrl && url.startsWith(_framesBaseUrl))
}

/**
 * Append SAS token to a resolved blob URL for actual fetching.
 * Handles both scan-data (COLMAP) and scan-frames (mobile) containers.
 */
export function toBlobUrl(url: string): string {
  if (!url) return url
  // An expired SAS is worse than none: it turns every image into a guaranteed
  // 403 and drives the thumbnail fallback through its whole candidate list.
  const framesSas = sasIsValid(_framesSasToken) ? _framesSasToken : cachedSas('dukkan_frames_sas_token')
  const blobSas = sasIsValid(_blobSasToken) ? _blobSasToken : cachedSas('dukkan_blob_sas_token')

  if (url.includes('scan-frames') && framesSas) {
    if (url.includes(framesSas.substring(0, 20))) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${framesSas}`
  }
  if (url.includes('scan-data') && blobSas) {
    if (url.includes(blobSas.substring(0, 20))) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${blobSas}`
  }
  return url
}

/**
 * Blob-aware fetch: for blob URLs, appends SAS token and strips Authorization
 * headers (Azure Blob Storage returns 400 if both SAS and auth header are sent).
 * For non-blob URLs, passes through unchanged.
 */
export function blobFetch(url: string, init?: RequestInit): Promise<Response> {
  if (!isBlobUrl(url)) {
    return fetch(url, init)
  }
  // Blob URL: add SAS token, strip Authorization header
  const finalUrl = toBlobUrl(url)
  if (init?.headers) {
    const h = new Headers(init.headers)
    h.delete('Authorization')
    return fetch(finalUrl, { ...init, headers: h })
  }
  return fetch(finalUrl, init)
}
